import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import {
  ACTION_ADD,
  ACTION_DELETE,
  ACTION_EDIT,
  ACTION_FINAL_SUBMIT,
  ACTION_NIL,
  NOT_SPECIFIED,
  OfficerDetails,
  PropertyRow,
  apiErrorMessage,
  buildOfficerFromApi,
  buildPayloadProperties,
  downloadAnnualPropertyPdf,
  emptyRow,
  extractOfficerAndRows,
  fetchNilPropertyDetails,
  fetchPropertyDetails,
  formatSapDisplayDate,
  getEmpId,
  getProfileOfficerDetails,
  inputValueToSapDate,
  isBlankPropertyRow,
  isNilPropertyRow,
  mapPropertyRows,
  sapDateToInputValue,
  saveAnnualProperty,
  syncAnnualProperty,
  triggerBrowserDownload,
  validateDraft,
} from "./annualPropertyReturn.service";

const CONSTRUCTION_NOTE =
  "For Construction Linked Properties, the probable date of acquisition which was initially given should be filled in Acquisitiondate column, However, if the property has not been acquired yet/No date of acquisition is known,the clarification for the same may be written in Remarks column.";

const CERTIFICATION_STATEMENT =
  "Certified that I have been regularly submitting the Immovable Property Returns and no return for any year is outstanding.";

type ConfirmState = {
  title: string;
  message: string;
  confirmText: string;
  onConfirm: () => void;
};

const showValue = (value: string) => value || NOT_SPECIFIED;

const AnnualPropertyReturn: React.FC = () => {
  const liveEmpId = useSelector(
    (state: any) => state.employeesState?.data?.data?.empId || ""
  );
  const profile = getProfileOfficerDetails();
  const [officer, setOfficer] = useState<OfficerDetails>(profile);
  const [properties, setProperties] = useState<PropertyRow[]>([]);
  const [signature, setSignature] = useState(profile.Name);
  const [submissionDate, setSubmissionDate] = useState("");
  const [isFinalSubmitted, setIsFinalSubmitted] = useState(false);
  const [nilPropertyDeclared, setNilPropertyDeclared] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFinalSubmitting, setIsFinalSubmitting] = useState(false);
  const [isDeclaringNil, setIsDeclaringNil] = useState(false);

  const [isOfficerExpanded, setIsOfficerExpanded] = useState(true);
  const [isPropertyExpanded, setIsPropertyExpanded] = useState(true);

  const [editorVisible, setEditorVisible] = useState(false);
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [draft, setDraft] = useState<PropertyRow>(emptyRow());
  const [draftErrors, setDraftErrors] = useState<Record<string, boolean>>({});

  const [apiErrorMessageText, setApiErrorMessageText] = useState("");
  const [apiErrorVisible, setApiErrorVisible] = useState(false);
  const [thankYouVisible, setThankYouVisible] = useState(false);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const loadSeq = useRef(0);

  const showApiError = (message: string) => {
    setApiErrorMessageText(message);
    setApiErrorVisible(true);
  };

  const loadDetails = useCallback(async () => {
    const empId = getEmpId();
    if (!empId) {
      toast.error("Employee ID not found");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const seq = ++loadSeq.current;
    try {
      const response = await fetchPropertyDetails(empId);
      if (seq !== loadSeq.current || getEmpId() !== empId) return;
      const profileOfficer = getProfileOfficerDetails();
      if (response.success && response.data) {
        const { officer: officerObj, rows: rawRows } = extractOfficerAndRows(response.data);
        const nextOfficer = buildOfficerFromApi(officerObj, profileOfficer);
        setOfficer(nextOfficer);
        if (nextOfficer.Name) setSignature(nextOfficer.Name);
        if (officerObj) {
          setSubmissionDate(formatSapDisplayDate(officerObj.SubmitionDate));
          setIsFinalSubmitted(String(officerObj.FinalSubmition ?? "").trim() === "01");
        }
        setProperties(mapPropertyRows(rawRows));
      } else if (response.message) {
        showApiError(response.message);
      }
    } catch (err: any) {
      if (seq !== loadSeq.current) return;
      showApiError(apiErrorMessage(err, "Failed to load property details."));
    } finally {
      if (seq === loadSeq.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const empId = getEmpId();
    loadDetails();
    if (!empId) return;
    fetchNilPropertyDetails(empId).catch(() => { });
  }, [loadDetails, liveEmpId]);

  const handleSync = async () => {
    const empId = getEmpId();
    if (!empId) {
      toast.error("Employee ID not found");
      return;
    }
    setIsSyncing(true);
    try {
      const res = await syncAnnualProperty(empId);
      if (res.success) toast.success(res.message || "Success");
      else toast.error(res.message || "Sync failed");
    } catch {
      toast.error("An unexpected error occurred");
    } finally {
      setIsSyncing(false);
    }
    await loadDetails();
  };

  const openAddEditor = () => {
    setEditingUid(null);
    setDraft(emptyRow());
    setDraftErrors({});
    setEditorVisible(true);
  };

  const openEditEditor = (row: PropertyRow) => {
    setEditingUid(row.__uid);
    setDraft({ ...row });
    setDraftErrors({});
    setEditorVisible(true);
  };

  const closeEditor = () => {
    setEditorVisible(false);
    setEditingUid(null);
    setDraft(emptyRow());
    setDraftErrors({});
  };

  const setDraftField = (field: keyof PropertyRow, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    if (draftErrors[field]) setDraftErrors((prev) => ({ ...prev, [field]: false }));
  };

  const applyDraft = async () => {
    if (isSaving) return;
    const errs = validateDraft(draft);
    setDraftErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fill all required fields.");
      return;
    }

    const empId = getEmpId();
    if (!empId) {
      toast.error("Employee ID not found");
      return;
    }

    const isNewRow = editingUid === null;
    let rowToSave: PropertyRow;
    let next: PropertyRow[];
    if (isNewRow) {
      const nextSeq = String(properties.length + 1).padStart(5, "0");
      rowToSave = {
        ...draft,
        __uid: draft.__uid,
        SequenceNumber: draft.SequenceNumber || nextSeq,
      };
      next = [...properties, rowToSave];
    } else {
      rowToSave = { ...draft, __uid: editingUid as string };
      next = properties.map((row) => (row.__uid === editingUid ? rowToSave : row));
    }

    setIsSaving(true);
    try {
      const res = await saveAnnualProperty({
        Action: isNewRow ? ACTION_ADD : ACTION_EDIT,
        ToTable: { results: buildPayloadProperties([rowToSave], empId) },
      });
      if (res.success) {
        setProperties(next);
        closeEditor();
        toast.success("Property details saved successfully.");
        await loadDetails();
      } else {
        showApiError(res.message || "Failed to save property details.");
      }
    } catch (err: any) {
      showApiError(apiErrorMessage(err, "Failed to save property details."));
    } finally {
      setIsSaving(false);
    }
  };

  const deleteProperty = (uid: string) => {
    const target = properties.find((row) => row.__uid === uid);
    setConfirmState({
      title: "Delete Property",
      message: "Are you sure you want to remove this property entry?",
      confirmText: "Delete",
      onConfirm: async () => {
        setConfirmState(null);
        setIsSaving(true);
        try {
          const res = await saveAnnualProperty({
            Action: ACTION_DELETE,
            SequenceNumber: target?.SequenceNumber || "00001",
          });
          if (res.success) {
            setProperties((prev) => prev.filter((row) => row.__uid !== uid));
            toast.success("Property removed successfully.");
            await loadDetails();
          } else {
            toast.error(res.message || "Failed to remove property.");
          }
        } catch (err: any) {
          toast.error(apiErrorMessage(err, "Failed to remove property."));
        } finally {
          setIsSaving(false);
        }
      },
    });
  };

  const handleFinalSubmit = async () => {
    setIsFinalSubmitting(true);
    try {
      const res = await saveAnnualProperty({
        Action: ACTION_FINAL_SUBMIT,
        SequenceNumber: properties[0]?.SequenceNumber || "00001",
      });
      if (res.success) {
        setIsFinalSubmitted(true);
        setThankYouVisible(true);
      } else {
        toast.error(res.message || "Failed to submit Annual Property Return.");
      }
    } catch (err: any) {
      toast.error(apiErrorMessage(err, "Failed to submit Annual Property Return."));
    } finally {
      setIsFinalSubmitting(false);
    }
  };

  const confirmFinalSubmit = () => {
    if (isFinalSubmitted) return;
    setConfirmState({
      title: "Final Submit",
      message:
        "Once finally submitted, you will not be able to edit your Annual Property Return. Do you want to continue?",
      confirmText: "Final Submit",
      onConfirm: () => {
        setConfirmState(null);
        handleFinalSubmit();
      },
    });
  };

  const declareNilProperty = async () => {
    if (isDeclaringNil) return;
    const empId = getEmpId();
    if (!empId) {
      toast.error("Employee ID not found");
      return;
    }
    setIsDeclaringNil(true);
    try {
      const res = await saveAnnualProperty({ Action: ACTION_NIL, EmpID: empId });
      if (res.success) {
        setNilPropertyDeclared(true);
        toast.success("Nil property declared successfully.");
        await handleSync();
      } else {
        showApiError(res.message || "Failed to declare nil property.");
      }
    } catch (err: any) {
      showApiError(apiErrorMessage(err, "Failed to declare nil property."));
    } finally {
      setIsDeclaringNil(false);
    }
  };

  const handleDownload = async () => {
    const empId = getEmpId();
    if (!empId) {
      toast.error("Employee ID not found");
      return;
    }
    setIsDownloading(true);
    try {
      const file = await downloadAnnualPropertyPdf(empId);
      triggerBrowserDownload(file.blob, file.fileName);
      toast.success("Downloaded successfully");
    } catch (err: any) {
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          toast.error(parsed?.message || parsed?.data?.message || "Download failed");
          return;
        } catch {
          toast.error("Download failed");
          return;
        }
      }
      toast.error(err?.message || "Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const canEdit = !isFinalSubmitted;
  const hasNoRealProperty =
    properties.length === 0 || (properties.length === 1 && isBlankPropertyRow(properties[0]));

  const officerFields: { label: string; value: string }[] = [
    { label: "Name of Officer", value: officer.Name },
    { label: "Employee Number", value: officer.EmployeeNumber },
    { label: "Birth Date", value: officer.BirthDate },
    { label: "Wing", value: officer.Wing },
    { label: "Department", value: officer.Department },
    { label: "Designation", value: officer.Designation },
    { label: "Pay Scale", value: officer.PayScale },
    { label: "Basic Pay", value: officer.BasicPay },
    { label: "Nature of Employment", value: officer.EmploymentType },
    { label: "Date of Joining", value: officer.JoiningDate },
  ];

  return (
    <div className="page-wrapper">
      <style>{aprStyles}</style>
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Annual Property Return</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                </li>
                    <li className="breadcrumb-item">
                      <Link to={all_routes.services.list}>Other HR Services</Link>
                    </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Annual Property Return
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleSync}
              disabled={isSyncing || isLoading}
            >
              <i className={`ti ti-refresh me-1 ${isSyncing ? "apr-spin" : ""}`}></i>
              {isSyncing ? "Syncing" : "Sync"}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleDownload}
              disabled={isDownloading || isLoading}
            >
              <i className="ti ti-download me-1"></i>
              {isDownloading ? "Downloading..." : "Download PDF"}
            </button>
            {canEdit && !isLoading && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmFinalSubmit}
                disabled={isSaving || isFinalSubmitting}
              >
                {isFinalSubmitting ? (
                  <span className="spinner-border spinner-border-sm me-1"></span>
                ) : (
                  <i className="ti ti-circle-check me-1"></i>
                )}
                Final Submit
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-danger" role="status"></div>
            </div>
          </div>
        ) : (
          <>
            {isFinalSubmitted && (
              <div className="apr-submitted mb-3">
                <i className="ti ti-circle-check"></i>
                <span>Finally submitted. Editing is disabled.</span>
              </div>
            )}

            <div className="card mb-3">
              <button
                type="button"
                className="apr-card-head"
                onClick={() => setIsOfficerExpanded((v) => !v)}
              >
                <span>Officer Details</span>
                <i className={`ti ${isOfficerExpanded ? "ti-chevron-up" : "ti-chevron-down"}`}></i>
              </button>
              {isOfficerExpanded && (
                <div className="card-body pt-0">
                  {officerFields.map((field) => (
                    <div className="apr-row" key={field.label}>
                      <div className="apr-label">{field.label}</div>
                      <div className="apr-value">{showValue(field.value)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {hasNoRealProperty && !nilPropertyDeclared && (
              <div className="text-center mb-3">
                <button
                  type="button"
                  className="btn btn-link apr-nil-btn"
                  onClick={declareNilProperty}
                  disabled={isDeclaringNil}
                >
                  {isDeclaringNil ? "Please wait..." : 'I HAVE "NIL" PROPERTY TO DECLARE'}
                </button>
              </div>
            )}

            {!nilPropertyDeclared && (
              <div className="card mb-3">
                <button
                  type="button"
                  className="apr-card-head"
                  onClick={() => setIsPropertyExpanded((v) => !v)}
                >
                  <span>Property Details ({properties.length})</span>
                  <i className={`ti ${isPropertyExpanded ? "ti-chevron-up" : "ti-chevron-down"}`}></i>
                </button>
                {isPropertyExpanded && (
                  <div className="card-body">
                    <div className="apr-note mb-3">{CONSTRUCTION_NOTE}</div>

                    {properties.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        <i className="ti ti-home fs-1 d-block mb-2"></i>
                        No property added yet.
                      </div>
                    ) : (
                      properties.map((row, index) => (
                        <div className="apr-property" key={row.__uid}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="apr-seq">#{row.SequenceNumber || String(index + 1)}</span>
                            {canEdit && !isNilPropertyRow(row) && (
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => openEditEditor(row)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => deleteProperty(row.__uid)}
                                  disabled={isSaving}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                          {[
                            ["Type of Property", row.TypeOfProperty],
                            ["Name / Detail & Location", row.NameDetailsAndLocationOfProperty],
                            ["Details (Commercial / Residential)", row.DetailsOfProperty],
                            ["Size & Area", row.SizeAndArea],
                            ["Acquisition Price", row.AcquisitionPrice],
                            ["Acquisition Date", formatSapDisplayDate(row.AcquisitionDateInDDMMYYYYFormatOnly)],
                            ["Present Value", row.PresentValue],
                            ["Owner Name with Relationship and Share in Property", row.OwnerNameWithRelationshipAndShareInPrope],
                            ["Mode of Acquisition", row.ModeOfAcquisition],
                            ["Annual Income from Property", row.AnnualIncomeFromProperty],
                            ["Remarks", row.Remarks],
                          ].map(([label, value]) => (
                            <div className="apr-field" key={label}>
                              <div className="apr-field-label">{label}</div>
                              <div className="apr-field-value">{showValue(value)}</div>
                            </div>
                          ))}
                        </div>
                      ))
                    )}

                    {canEdit && (
                      <button type="button" className="btn btn-outline-danger mt-2" onClick={openAddEditor}>
                        <i className="ti ti-plus me-1"></i>
                        Add Property
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="card mb-3">
              <div className="card-body">
                <h5 className="apr-cert-title">Certification</h5>
                <p className="mb-3">{CERTIFICATION_STATEMENT}</p>
                <div className="apr-sign">{showValue(signature || officer.Name)}</div>
                {!!submissionDate && <div className="text-muted">{submissionDate}</div>}
              </div>
            </div>
          </>
        )}
      </div>

      {isSyncing && (
        <div className="apr-overlay">
          <div className="spinner-border text-light" role="status"></div>
        </div>
      )}

      {editorVisible && (
        <div className="apr-modal-backdrop" onClick={closeEditor}>
          <div className="apr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="apr-modal-header">
              <h5 className="mb-0">{editingUid === null ? "Add Property" : "Edit Property"}</h5>
              <button type="button" className="btn-close btn-close-white" onClick={closeEditor} aria-label="Close"></button>
            </div>
            <div className="apr-modal-body">
              <Field
                label="Type of Property"
                required
                error={draftErrors.TypeOfProperty}
                value={draft.TypeOfProperty}
                placeholder="Land / Apartment / Building / Other"
                onChange={(v) => setDraftField("TypeOfProperty", v)}
              />
              <Field
                label="Name / Detail & Location"
                required
                multiline
                error={draftErrors.NameDetailsAndLocationOfProperty}
                value={draft.NameDetailsAndLocationOfProperty}
                placeholder="Specify city & state"
                onChange={(v) => setDraftField("NameDetailsAndLocationOfProperty", v)}
              />
              <Field
                label="Details of Property"
                required
                error={draftErrors.DetailsOfProperty}
                value={draft.DetailsOfProperty}
                placeholder="Commercial / Residential / Other"
                onChange={(v) => setDraftField("DetailsOfProperty", v)}
              />
              <Field
                label="Size & Area"
                required
                error={draftErrors.SizeAndArea}
                value={draft.SizeAndArea}
                placeholder="Specify sq. mt. or sq. ft."
                onChange={(v) => setDraftField("SizeAndArea", v)}
              />
              <Field
                label="Acquisition Price"
                required
                error={draftErrors.AcquisitionPrice}
                value={draft.AcquisitionPrice}
                placeholder="0"
                onChange={(v) => setDraftField("AcquisitionPrice", v)}
              />
              <div className="mb-3">
                <label className="form-label">
                  Acquisition Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control ${draftErrors.AcquisitionDateInDDMMYYYYFormatOnly ? "is-invalid" : ""}`}
                  value={sapDateToInputValue(draft.AcquisitionDateInDDMMYYYYFormatOnly)}
                  onChange={(e) =>
                    setDraftField(
                      "AcquisitionDateInDDMMYYYYFormatOnly",
                      inputValueToSapDate(e.target.value)
                    )
                  }
                />
                <div className="form-text">DD.MM.YYYY</div>
                {draftErrors.AcquisitionDateInDDMMYYYYFormatOnly && (
                  <div className="invalid-feedback d-block">Required</div>
                )}
              </div>
              <Field
                label="Present Value"
                required
                error={draftErrors.PresentValue}
                value={draft.PresentValue}
                placeholder="0"
                onChange={(v) => setDraftField("PresentValue", v)}
              />
              <Field
                label="Owner Name with Relationship & Share in Property"
                required
                multiline
                error={draftErrors.OwnerNameWithRelationshipAndShareInPrope}
                value={draft.OwnerNameWithRelationshipAndShareInPrope}
                onChange={(v) => setDraftField("OwnerNameWithRelationshipAndShareInPrope", v)}
              />
              <Field
                label="Mode of Acquisition"
                required
                error={draftErrors.ModeOfAcquisition}
                value={draft.ModeOfAcquisition}
                onChange={(v) => setDraftField("ModeOfAcquisition", v)}
              />
              <Field
                label="Annual Income from Property"
                value={draft.AnnualIncomeFromProperty}
                placeholder="0"
                onChange={(v) => setDraftField("AnnualIncomeFromProperty", v)}
              />
              <Field
                label="Remarks / Additional Information"
                multiline
                large
                value={draft.Remarks}
                placeholder="Property acquired vide which office order, in DMRC / previous organisation"
                onChange={(v) => setDraftField("Remarks", v)}
              />
            </div>
            <div className="apr-modal-footer">
              <button type="button" className="btn btn-light" onClick={closeEditor} disabled={isSaving}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={applyDraft} disabled={isSaving}>
                {isSaving ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : editingUid === null ? (
                  "Add"
                ) : (
                  "Update"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmState && (
        <div className="apr-modal-backdrop" onClick={() => setConfirmState(null)}>
          <div className="apr-dialog" onClick={(e) => e.stopPropagation()}>
            <h5 className="mb-2">{confirmState.title}</h5>
            <p className="mb-3">{confirmState.message}</p>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light" onClick={() => setConfirmState(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmState.onConfirm}>
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {apiErrorVisible && (
        <div className="apr-modal-backdrop" onClick={() => setApiErrorVisible(false)}>
          <div className="apr-dialog text-center" onClick={(e) => e.stopPropagation()}>
            <i className="ti ti-alert-circle text-danger fs-1"></i>
            <p className="mt-2 mb-3">{apiErrorMessageText}</p>
            <button type="button" className="btn btn-danger" onClick={() => setApiErrorVisible(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {thankYouVisible && (
        <div className="apr-modal-backdrop">
          <div className="apr-dialog text-center">
            <i className="ti ti-circle-check text-success apr-success-icon"></i>
            <h5 className="mt-2">Thank You</h5>
            <p>Your Annual Property Return has been submitted successfully.</p>
            <button type="button" className="btn btn-danger" onClick={() => setThankYouVisible(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  multiline?: boolean;
  large?: boolean;
}> = ({ label, value, onChange, placeholder, required, error, multiline, large }) => (
  <div className="mb-3">
    <label className="form-label">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {multiline ? (
      <textarea
        className={`form-control ${error ? "is-invalid" : ""}`}
        rows={large ? 4 : 3}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <input
        type="text"
        className={`form-control ${error ? "is-invalid" : ""}`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    )}
    {error && <div className="invalid-feedback d-block">Required</div>}
  </div>
);

const aprStyles = `
.apr-card-head {
  width: 100%;
  border: 0;
  background: #fff;
  border-left: 4px solid #e11d2e;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  font-weight: 600;
  text-align: left;
}
.apr-row { padding: 8px 0; border-bottom: 1px solid #eee; }
.apr-label { color: #e11d2e; font-weight: 600; font-size: 13px; }
.apr-value { color: #1f2937; }
.apr-property { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; margin-bottom: 12px; }
.apr-seq { background: #fde8ea; color: #e11d2e; font-weight: 700; border-radius: 6px; padding: 2px 8px; }
.apr-field { padding: 6px 0; border-bottom: 1px solid #f3f4f6; }
.apr-field-label { color: #6b7280; font-size: 12px; }
.apr-field-value { color: #111827; }
.apr-note {
  background: #f3f4f6;
  color: #4b5563;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  line-height: 1.5;
}
.apr-submitted {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #e7f6ec;
  color: #17803d;
  border-left: 4px solid #17803d;
  border-radius: 8px;
  padding: 10px 12px;
  font-weight: 600;
}
.apr-nil-btn { font-weight: 600; text-decoration: underline; }
.apr-cert-title { color: #e11d2e; font-weight: 600; }
.apr-sign { font-weight: 600; }
.apr-table { font-size: 13px; min-width: 1400px; }
.apr-table th { background: #f8f9fa; white-space: nowrap; }
.apr-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
}
.apr-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  z-index: 1090;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.apr-modal, .apr-dialog {
  background: #fff;
  border-radius: 12px;
  width: min(720px, 100%);
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.apr-dialog { width: min(460px, 100%); padding: 20px; display: block; }
.apr-modal-header {
  background: #e11d2e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
}
.apr-modal-body { padding: 16px; overflow: auto; }
.apr-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
}
.apr-success-icon { font-size: 48px; }
.apr-spin { animation: apr-spin 1s linear infinite; display: inline-block; }
@keyframes apr-spin { to { transform: rotate(360deg); } }
`;

export default AnnualPropertyReturn;
